<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewSite || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Sites - {{ filteredSites.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddSite || user?.is_staff"
          id="add-site-btn"
          @click="goToAddSite"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
        >
          Add Site
        </button>
        <button
          @click="showExportDialog = true"
          class="btn-secondary px-6 py-2.5"
        >
          Download CSV
        </button>
      </div>
      <CsvExportDialog
        :is-open="showExportDialog"
        :available-columns="columns"
        :data="filteredSites"
        @close="showExportDialog = false"
      />
    </div>

        <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading sites..."
      @select="handleOrgSelect"
    />


    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search sites by name..."
        />
      </div>

      <!-- Filters Button (for mobile screens) -->
      <div class="mb-4 md:hidden">
        <button
          @click="showFilters = !showFilters"
          class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          {{ showFilters ? "Hide Filters" : "Show Filters" }}
        </button>
      </div>

      <!-- Filters Section -->
      <div
        :class="[
          'md:flex md:items-center ',
          showFilters ? 'block' : 'hidden md:block',
        ]"
      >
        <FiltersSection
          :filters="dynamicFilters"
          v-model:selectedFilters="selectedFilters"
          :showFilters="showFilters"
          @update:showFilters="showFilters = $event"
          @applyFilters="applyFilters"
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteSite || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedSites.length === 0"
          :class="[
            bulkAction && selectedSites.length
              ? 'btn-danger'
              : 'btn-secondary opacity-50 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Sites Table -->
    <GenericTable
      id="sites-table"
      ref="table"
      :columns="columns"
      :items="paginatedSites"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedSites"
      @rowClick="goToSiteDetail"
    >
      <!-- Custom cell templates -->
      <template #org="{ item }">
        {{ tenant.getOrganizationName(item.org) }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #in_maintenance="{ value }">
        <span
          :class="[
            'px-2 py-1 rounded-full text-xs font-semibold',
            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
          ]"
        >
          {{ value ? "Yes" : "No" }}
        </span>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <!-- Pagination Buttons -->
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
    />

  </div>
</template>

<script>
import { getSites, deleteSite } from "@/services/siteService";
import { getAllTunnels } from "@/services/tunnelService";
import SearchBar from "@/components/SearchBar.vue";
import FiltersSection from "@/components/FiltersSection.vue";
import GenericTable from "@/components/GenericTable.vue";
import CsvExportDialog from "@/components/CsvExportDialog.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { getAllProbes } from "@/services/probeService";
import { getAllSubnets } from "@/services/subnetService";
import { getAllVlans } from "@/services/vlanService";
import { getAllBlackboxes } from "@/services/blackboxService";
import { getAllLoadsheddings } from "@/services/loadshedding-service";
import DeleteModal from "@/components/DeleteModal.vue";
import { useTutorial } from "@/composables/tutorial";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "SiteView",
  components: {
    SearchBar,
    FiltersSection,
    GenericTable,
    CsvExportDialog,
    PageError,
    DeleteModal,
    OrganizationTabs,
    GlassSelect,
  },
  data() {
    return {
      sites: [],
      searchQuery: "",
      filteredSites: [],
      currentPage: 1,
      itemsPerPage: 10,
      showFilters: false,
      deletingItem: false,
      isDeleting: false,
      selectedFilters: {
        maintenanceFilter: "",
        organizationFilter: "",
      },
      selectedSites: [],
      bulkAction: "",
      dynamicFilters: [],
      vlans:[],
      subnets:[],
      blackboxes:[],
      tunnels:[],
      loadshedding:[],
      probes:[],
      showExportDialog: false,
      columns: [
        { key: "name", label: "Name", sortable: true },
        { key: "org", label: "Organisation", sortable: true },
        { key: "created_date", label: "Created Date", sortable: true },
        { key: "in_maintenance", label: "In Maintenance", sortable: true },
      ],
      canAddSite: false,
      canDeleteSite: false,
      canViewSite: false,
      error: "",
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server.",
        422 : "Malformed request. Failed to load Sites!"
      }
    };
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    }, 
    isFiltered() {
      return (
        this.searchQuery !== "" ||
        this.selectedFilters.maintenanceFilter !== "" ||
        this.selectedFilters.organizationFilter !== ""
      );
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredSites.length / this.itemsPerPage);
    },
    paginatedSites() {
      if (this.itemsPerPage === -1) {
        return this.filteredSites;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredSites.slice(start, end);
    },
    componetObjects() { 
      return this.selectedSites.map(site => {
        return {
          values: [
            { name: "Site", value: site.name },
            { name: "Tunnels", value: site.related_objects.tunnel_count },
            { name: "Loadshedding", value: site.related_objects.loadshedding_count },
            { name: "Probes", value: site.related_objects.probe_count },
            { name: "Subnet", value: site.related_objects.subnet_count },
            { name: "VLANs", value: site.related_objects.vlan_count },
            { name: "Endpoint Availability", value: site.related_objects.blackbox_count },
            { name: 'Fortigates', value: site.related_objects.fortigate_count},
            { name: "SNMPs", value: site.related_objects.snmp_count},
            { name: "Network Devices", value: site.related_objects.network_device_count }
          ]
        };
      });
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true 
  },
  searchQuery() {
      this.applyFilters();
    },   
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
        await this.tenant.syncUrlParam();
        await this.fetchSites(this.tenant.currentSchemaName.value);
    } else {
        await this.tenant.loadInitialData(this.fetchSites);
    }
    this.initializeFilters();
  },
  methods: {
    async startTutorial() {
      const siteTutorialSteps = [
        { element: '#searchbar', action: 'display', message: 'Use the search bar to filter sites by name.' },
        { element: '#sites-table', action: 'display', message: 'The sites table lists all sites. Click a row to view its details.' },
        { element: '#add-site-btn', action: 'display', message: 'Click here to add a new site.' },
      ];
      await this._start(siteTutorialSteps);
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddSite = await useStore.hasPermission("Can add site");
        this.canDeleteSite = await useStore.hasPermission("Can delete site");
        this.canViewSite = await useStore.hasPermission("Can view site");
      }
    },
    goToAddSite() {
      this.$router.push({ 
        name: "AddSite",
        query: this.tenant.addSchemaParam()
      });
    },
    
    async handleOrgSelect(orgId) {
      try {
        await this.tenant.switchOrganization(orgId, this.fetchSites);
      } catch (error) {
        this.error = "Failed to switch organization";
      }
    },

    async fetchSites(schemaName=null) {
        try {
          if (!schemaName) {
            schemaName = this.tenant.currentSchemaName.value;
          }
          const sites = await getSites(schemaName);
          this.sites = [...sites];
          this.applyFilters();
      } catch (error) {
          if (error.response) {
            this.error = `${this.errorMessages[error.response.status]}`;
            this.errorCode = `${error.response.status}`;
          } else{
            this.error = `Unknown Error`
          }
      }
    },
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs.reduce((acc, org) => {
          acc[org.id] = org.name;
          return acc;
        }, {});
        // Reinitialize filters once organizations are fetched
        this.initializeFilters();
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },
    goToSiteDetail(site) {
      this.$router.push({ 
        name: "SiteDetail", 
        params: { id: site.id },
        query: this.tenant.getDetailPageParam()
      });
    },
    initializeFilters() {
      this.dynamicFilters = [
        {
          name: "maintenanceFilter",
          label: "Site in Maintenance",
          options: [
            { value: "", label: "All" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ],
        },
        {
          name: "organizationFilter",
          label: "Organisation",
          options: [{ value: "", label: "All" }].concat(
            this.tenant.organizations.value.map((org) => ({
                value: String(org.id),
                label: org.name,
            }))
          ),
        },
      ];
    },
    applyFilters() {
      this.filteredSites = this.sites.filter((site) => {
        const nameMatch = site.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());

        const maintenanceMatch =
          this.selectedFilters.maintenanceFilter === "" ||
          site.in_maintenance.toString() ===
            this.selectedFilters.maintenanceFilter;

        const organizationMatch =
          this.selectedFilters.organizationFilter === "" ||
          site.org.toString() === this.selectedFilters.organizationFilter;

        return nameMatch && maintenanceMatch && organizationMatch;
      });

      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    updateSelectedSites(selectedItems) {
      this.selectedSites = selectedItems.filter((item => !item.marked_for_deletion));
    },
    getOrganizationName(orgId) {
      return this.organizations[orgId] || `redacted`;
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    async deleteSelectedSites() {
      if (this.selectedSites.length === 0) {
        alert("No sites selected for deletion");
        return;
      }

      try {
        // check if staff user and schema is provided
        const schemaName = this.tenant.currentSchemaName.value;
        if (!schemaName) {
          this.error = "Malformed request. Failed to delete selected sites!";
          this.errorCode = "422!";
          return;
        }
        
        const deletePromises = this.selectedSites.map((site) =>
          deleteSite(site.id, schemaName)
        );
        await Promise.all(deletePromises);

        await this.fetchSites()
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedSites = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting sites. Please try again.");
      } finally {
        this.isDeleting = false;
      }
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true
      await this.deleteSelectedSites();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
  },
};
</script>


<style>
@keyframes loading {
  0% {
    width: 0%;
    margin-left: 0%;
  }
  50% {
    width: 50%;
    margin-left: 25%;
  }
  100% {
    width: 0%;
    margin-left: 100%;
  }
}
</style>
